import {
  ArrowLeft,
  Bell,
  CheckCircle2,
  FileCheck2,
  FolderOpen,
  Inbox,
  LayoutDashboard,
  MessageCircle,
  Settings,
  ShieldCheck,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';

export function WorkspaceShell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [agencyProfile, setAgencyProfile] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    if (!user?.agencyId) return;
    api.get<{ profile: Record<string, any> }>('/api/agency/dashboard').then(result => setAgencyProfile(result.profile)).catch(() => undefined);
  }, [user?.agencyId]);

  const initials = user?.name.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || 'AG';
  const agencyName = String(agencyProfile?.name || 'Agency Operations');
  const agencyInitials = agencyName.split(/\s+/).slice(0, 2).map(word => word[0]).join('').toUpperCase() || 'AG';
  const agencyLogoUrl = agencyProfile?.logo_key ? `/media/${agencyProfile.logo_key}` : '';

  const agencyNav = [
    { to: '/agency', label: 'Overview', icon: LayoutDashboard, exact: true },
    { to: '/agency/hostels', label: 'Hostels & Offers', icon: FolderOpen },
    { to: '/agency/requests', label: 'Student Leads', icon: Inbox },
    { to: '/agency/messages', label: 'Conversations', icon: MessageCircle },
    { to: '/agency/verification', label: 'Business Pass', icon: FileCheck2 },
    { to: '/agency/settings', label: 'Agency Settings', icon: Settings },
  ];

  return (
    <div className="workspace-shell">
      <aside className="workspace-sidebar">
        <div>
          <div className="flex items-center gap-3 px-2 py-1">
            <Logo compact />
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--accent-text)] bg-[var(--accent-soft)] px-2 py-0.5 rounded-[var(--radius-pill)]">
              Agency
            </span>
          </div>

          <nav className="workspace-sidebar-nav" aria-label="Agency operations navigation">
            {agencyNav.map(({ to, label, icon: Icon, exact }) => (
              <NavLink
                key={to}
                to={to}
                end={exact}
                className={({ isActive }) => `workspace-nav-item ${isActive ? 'active' : ''}`}
              >
                <Icon size={18} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="pt-4 border-t border-[var(--border-subtle)] flex flex-col gap-2">
          <button
            className="workspace-nav-item w-full justify-start text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            onClick={() => navigate('/')}
          >
            <ArrowLeft size={18} />
            <span>View Marketplace</span>
          </button>
        </div>
      </aside>

      <div className="workspace-main">
        <header className="workspace-topbar">
          <div className="flex items-center gap-3">
            <div className="workspace-agency-avatar">{agencyLogoUrl ? <img src={agencyLogoUrl} alt=""/> : agencyInitials}</div>
            <div>
              <h1 className="text-sm font-semibold text-[var(--text-primary)]">{agencyName}</h1>
              <p className="text-xs text-[var(--text-tertiary)]">Agency workspace</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              className="flex items-center justify-center w-9 h-9 text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-full transition-colors"
              aria-label="Notifications"
              onClick={() => navigate('/notifications')}
            >
              <Bell size={18} />
            </button>
            <ThemeToggle />
            <button
              className="w-8 h-8 rounded-full bg-[var(--text-primary)] text-[var(--bg-surface)] flex items-center justify-center text-xs font-bold shrink-0"
              onClick={() => navigate('/settings')}
            >
              {initials}
            </button>
          </div>
        </header>

        <main className="workspace-content">
          <Outlet />
        </main>
      </div>
      <nav className="workspace-mobile-nav" aria-label="Agency mobile navigation">
        {agencyNav.map(({ to, label, icon: Icon, exact }) => (
          <NavLink key={to} to={to} end={exact} title={label} className={({ isActive }) => isActive ? 'active' : ''}>
            <Icon size={19}/><span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
